import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Scissors, Sun, Plus } from "lucide-react";
import { cn } from "../../lib/utils";
import { getSettings } from "../../services/settings";
import { useAuth } from "../../auth/AuthContext";

interface AddSaleDropdownProps {
  onAction: (action: "Services" | "Sunbeds") => void;
}

const AddSaleDropdown: React.FC<AddSaleDropdownProps> = ({ onAction }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSunbedActive, setIsSunbedActive] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch sunbed visibility setting
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        if (response.success && response.data?.settings) {
          // Check if sunbed is active (is_sunbed = 1 means active)
          const isSunbed = response.data.settings.is_sunbed;
          setIsSunbedActive(Number(isSunbed) === 1 || Number(isSunbed) > 0);
        }
      } catch (error) {
        console.error("Failed to fetch settings for sale dropdown:", error);
        // Default to false if settings can't be loaded
        setIsSunbedActive(false);
      }
    };

    fetchSettings();
  }, []);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const menuVariants: any = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] }
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 bg-[#885ABB] text-white px-4 sm:px-6 py-3 rounded-lg hover:bg-[#7748A4] transition h-auto border-none focus:outline-none ring-0 text-xs sm:text-sm shadow-sm",
          isOpen && "bg-[#7748A4]"
        )}
      >
        <span className="font-bold">Sale</span>
        <ChevronDown
          className={cn("h-4 w-4 text-white transition-transform duration-200", isOpen && "rotate-180")}
          strokeWidth={4}
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={menuVariants}
            className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[100] origin-top-right"
          >
            <div className="p-2 space-y-1">
              {/* Services Option */}
              <motion.button
                variants={itemVariants}
                whileHover={{ backgroundColor: "rgba(136, 90, 187, 0.08)" }}
                onClick={() => {
                  onAction("Services");
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left text-gray-700 rounded-xl transition-colors duration-200 group"
              >
                <div className="bg-[#885ABB]/10 p-2 rounded-lg group-hover:bg-[#885ABB] transition-colors duration-200">
                  <Scissors size={20} className="text-[#885ABB] group-hover:text-white transition-colors duration-200" />
                </div>
                <div>
                  <div className="font-bold text-sm text-gray-900">Services</div>
                  <div className="text-[10px] text-gray-400 font-medium">Add a standard service sale</div>
                </div>
              </motion.button>

              {/* Sunbeds Option - Only show if sunbed visibility is active */}
              {isSunbedActive && (
                <motion.button
                  variants={itemVariants}
                  whileHover={{ backgroundColor: "rgba(136, 90, 187, 0.08)" }}
                  onClick={() => {
                    onAction("Sunbeds");
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left text-gray-700 rounded-xl transition-colors duration-200 group"
                >
                  <div className="bg-[#885ABB]/10 p-2 rounded-lg group-hover:bg-[#885ABB] transition-colors duration-200">
                    <Sun size={20} className="text-[#885ABB] group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-gray-900">Sunbeds</div>
                    <div className="text-[10px] text-gray-400 font-medium">Add a tanning session sale</div>
                  </div>
                </motion.button>
              )}
            </div>

            {/* Subtle Footer/Decoration */}
            <div className="h-1 bg-gradient-to-r from-transparent via-[#885ABB]/20 to-transparent" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddSaleDropdown;
